/* Phase 18 — Lightweight guided tour overlay.

Keyboard: Esc → close. Buttons: Zurück / Überspringen / Weiter / Fertig.
Highlights each target via a transparent cutout + emerald outline.
Steps reference nav data-testids; the overlay re-measures on scroll/resize.
*/
import { useEffect, useLayoutEffect, useState } from "react";

const STEPS = [
  { testid: "project-switcher-trigger",
    title: "Projekte wechseln",
    body: "Alles ist nach Projekten getrennt. Hier wechselst du zwischen deinen Projekten oder legst neue an." },
  { testid: "nav-dashboard-link",
    title: "Dashboard",
    body: "Deine Startseite: die wichtigsten Zahlen deines Projekts auf einen Blick." },
  { testid: "nav-produkte-link",
    title: "Produkte",
    body: "Dein Katalog. Der KI-Agent nutzt diese Daten für Werbung, Videos und Seiten." },
  { testid: "nav-ki-agent-link",
    title: "KI-Agent",
    body: "Sag einfach, was du brauchst — Aktionen immer mit deiner Freigabe." },
  { testid: "nav-skills-link",
    title: "Skills",
    body: "Automatische Aufgaben mit Zeitplan — laufen auch ohne dich im Hintergrund." },
  { testid: "nav-social-link",
    title: "Social",
    body: "Beiträge erstellen, zeitplanen und auf allen Kanälen veröffentlichen." },
  { testid: "nav-einstellungen-link",
    title: "Einstellungen",
    body: "Not-Stopp, Limits, Verbindungen und E-Mail. Alle Kontrollen an einem Ort." },
];

const FLAG_KEY = "rundgang_v1_done";

export function tourWasOffered() {
  try { return !!localStorage.getItem(FLAG_KEY); } catch { return true; }
}
export function markTourOffered() {
  try { localStorage.setItem(FLAG_KEY, "1"); } catch { /* ignore */ }
}

export default function GuidedTour({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const current = STEPS[step];

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${current.testid}"]`);
      if (!el) { setRect(null); return; }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, step, current]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setStep((s) => Math.min(STEPS.length - 1, s + 1));
      if (e.key === "ArrowLeft") setStep((s) => Math.max(0, s - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const last = step === STEPS.length - 1;

  const card = rect
    ? {
        position: "fixed",
        top: Math.min(window.innerHeight - 240, Math.max(16, rect.top)),
        left: Math.min(window.innerWidth - 340, rect.left + rect.width + 16),
      }
    : { position: "fixed", top: "40vh", left: "40vw" };

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour">
      {/* Dim background */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      {/* Spotlight */}
      {rect && (
        <div
          data-testid="guided-tour-spotlight"
          style={{
            position: "fixed",
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.6), 0 0 0 2px rgb(16 185 129)",
            borderRadius: 12,
            pointerEvents: "none",
            transition: "all 180ms ease",
          }}
        />
      )}
      {/* Explanation card */}
      <div style={card}
        className="w-80 rounded-xl bg-[#111827] border border-white/10 shadow-2xl p-4 text-slate-200"
        data-testid="guided-tour-card">
        <div className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1" data-testid="guided-tour-step">
          Rundgang · Schritt {step + 1} von {STEPS.length}
        </div>
        <div className="text-base font-bold text-white mb-1">{current.title}</div>
        <div className="text-xs text-slate-300 leading-relaxed mb-4">{current.body}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => { markTourOffered(); onClose(); }}
            data-testid="guided-tour-skip"
            className="text-[11px] text-slate-500 hover:text-slate-300">Überspringen</button>
          <div className="flex-1" />
          {step > 0 && (
            <button onClick={() => setStep(step - 1)}
              data-testid="guided-tour-prev"
              className="px-2 py-1 text-xs rounded border border-white/10 text-slate-300 hover:bg-white/5">
              Zurück
            </button>
          )}
          <button
            onClick={() => {
              if (last) { markTourOffered(); onClose(); }
              else setStep(step + 1);
            }}
            data-testid="guided-tour-next"
            className="px-3 py-1 text-xs rounded bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
            {last ? "Fertig" : "Weiter"}
          </button>
        </div>
      </div>
    </div>
  );
}
